"use client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSelector } from "react-redux";

type SearchEntry = {
  id: string;
  title?: string | null;
  screen_id: string;
  prompt?: string | null;
  createdAt: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const SearchChatHistoryPopup = ({ isOpen, onClose }: Props) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchEntry[]>([]);
  const [localUpdates, setLocalUpdates] = useState<
    Record<string, Partial<SearchEntry>>
  >({});
  const { userData, isCreateModeifyWebsite, activeTab } = useSelector(
    (state) => state.main
  );
  const router = useRouter();
  const userId = userData._id;

    // const chatType = activeTab === "Card Maker" ? "card_maker" : activeTab === "Business Plan"
    //                         ? "business_plan" : "page_builder";
    const chatType = activeTab === "Card Maker" ? "card_maker" : activeTab === "Page Builder" ? "page_builder"
                     : activeTab?.toLowerCase().replace(/[\s\/]+/g, "_");

  useEffect(() => {
    if (!userId) return;

    const fetchHistory = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/all/${userId}?chat_type=${chatType}`,
          {
            credentials: "include",
          }
        );

        if (!res.ok) {
          throw new Error(`Request failed: ${res.status}`);
        }

        const data = await res.json();
        if (data.status === 1 && Array.isArray(data.details)) {
          const resultsWithLocalUpdates = data.details.map((item) => ({
            ...item,
            ...localUpdates[item.screen_id],
          }));
          setResults(resultsWithLocalUpdates);
        } else {
          setResults([]);
        }
      } catch (err) {
        console.error("Failed to fetch history", err);
      }
    };

    fetchHistory();
  }, [userId, isCreateModeifyWebsite, localUpdates]);

  useEffect(() => {
    if (!isOpen) return;

    const controller = new AbortController();

    const timeout = setTimeout(async () => {
      try {
        const endpoint =
          query.trim() === ""
            ? `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/all/${userId}?chat_type=${chatType}`
            : `${
                process.env.NEXT_PUBLIC_APP_URL_LOCAL
              }/api/website/search-chats?chat_type=${chatType}&user_id=${userId}&query=${encodeURIComponent(
                query
              )}`;

        const res = await fetch(endpoint, { signal: controller.signal });
        const data = await res.json();

        if (data.status === 1 && Array.isArray(data.details || data.results)) {
          const raw = data.details || data.results;
          const uniqueResults = raw
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
            .reduce((acc: Record<string, SearchEntry>, curr) => {
              if (!acc[curr.screen_id]) acc[curr.screen_id] = curr;
              return acc;
            }, {});

          // Apply local updates to the fetched data
          const resultsWithLocalUpdates = Object.values(uniqueResults).map(
            (item) => ({
              ...item,
              ...localUpdates[item.screen_id],
            })
          );

          setResults(resultsWithLocalUpdates);
        } else {
          setResults([]);
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error("Search error:", err);
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query, isOpen, userId, localUpdates]);
  useEffect(() => {
    if (!isOpen) {
      setQuery(""); 
    }
  }, [isOpen]);

  // Listen for chat rename events to update search results
  useEffect(() => {
    const handleChatRenamed = (event) => {
      const { screenId, newTitle } = event.detail;
      setLocalUpdates((prev) => ({
        ...prev,
        [screenId]: { ...prev[screenId], title: newTitle },
      }));

      // Update the results immediately
      setResults((prev) =>
        prev.map((item) =>
          item.screen_id === screenId ? { ...item, title: newTitle } : item
        )
      );
    };

    window.addEventListener("chatRenamed", handleChatRenamed);
    return () => window.removeEventListener("chatRenamed", handleChatRenamed);
  }, []);

  if (!isOpen) return null;
  const isToday = (d: Date) => {
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  };

  const hasQuery = query.trim() !== "";

  // Only split by date when no search query
  const todayEntries = !hasQuery
    ? results.filter((e) => isToday(new Date(e.createdAt)))
    : [];

  const pastEntries = !hasQuery
    ? results.filter((e) => !isToday(new Date(e.createdAt)))
    : [];

  const formatRelativeDate = (dateString: string) => {
    const d = new Date(dateString);

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isToday =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate();

    const isYesterday =
      d.getFullYear() === yesterday.getFullYear() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getDate() === yesterday.getDate();
    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";

    return d.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
    }); 
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-xl h-[500px] flex flex-col overflow-hidden w-[calc(100%-30px)]">
        {/* Header + Search */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <input
            type="text"
            // placeholder="Search chats..."
            placeholder={
              chatType === "card_maker"
                ? "Search Card Maker chats..."
                :`Search ${activeTab} chats...`
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="custom-search-chats w-full bg-transparent border-none outline-none text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus-visible:shadow-none"
          />
          <button
            onClick={onClose}
            className="ml-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
          >
            <svg
              width="20"
              height="20"
              fill="currentColor"
              className="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white"
            >
              <path d="M14.25 4.75a.75.75 0 011.06 1.06L11.06 10l4.25 4.25a.75.75 0 11-1.06 1.06L10 11.06l-4.25 4.25a.75.75 0 11-1.06-1.06L8.94 10 4.69 5.75a.75.75 0 011.06-1.06L10 8.94l4.25-4.19z" />
            </svg>
          </button>
        </div>

        {/* Results */}
        <div className="grow overflow-y-auto px-2 py-2">
          <ol>
            {hasQuery ? (
              // Flat list when searching
              results.map((entry) => (
                <li key={entry.id}>
                  <div
                    className="group cursor-pointer group flex items-center rounded-xl px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                    onClick={() => {
                      onClose();
                      router.push(`/${entry.screen_id}`);
                    }}
                  >
                    <div className="grow overflow-hidden whitespace-nowrap ps-2">
                      <div className="text-sm text-gray-800 dark:text-white">
                        {entry.title || entry.prompt || "Untitled Conversation"}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {entry.prompt}
                      </div>
                    </div>
                    <div className="hidden group-hover:block text-[13px] pl-4">
                      {entry.createdAt
                        ? formatRelativeDate(entry.createdAt)
                        : ""}
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <>
                {/* Today entries */}
                {todayEntries.length > 0 && (
                  <>
                    <li className="text-xs text-gray-500 dark:text-gray-400 px-4 pt-2">
                      Today
                    </li>
                    {todayEntries.map((entry) => (
                      <li key={entry.id}>
                        <div
                          className="cursor-pointer group flex items-center rounded-xl px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                          onClick={() => {
                            onClose();
                            router.push(`/${entry.screen_id}`);
                          }}
                        >
                          <div className="grow overflow-hidden whitespace-nowrap ps-2">
                            <div className="text-sm text-gray-800 dark:text-white">
                              {entry.title ||
                                entry.prompt ||
                                "Untitled Conversation"}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </>
                )}

                {/* Past entries */}
                {pastEntries.length > 0 && (
                  <>
                    <li className="text-xs text-gray-500 dark:text-gray-400 px-4 pt-2">
                      Previous 7 Days
                    </li>
                    {pastEntries.map((entry) => (
                      <li key={entry.id}>
                        <div
                          className="cursor-pointer group flex items-center rounded-xl px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                          onClick={() => {
                            onClose();
                            router.push(`/${entry.screen_id}`);
                          }}
                        >
                          <div className="grow overflow-hidden whitespace-nowrap ps-2">
                            <div className="text-sm text-gray-800 dark:text-white">
                              {entry.title ||
                                entry.prompt ||
                                "Untitled Conversation"}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </>
                )}
              </>
            )}

            {/* No results */}
            {results.length === 0 && (
              <li>
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {hasQuery
                    ? "No results found."
                    : "Start typing to search your chats..."}
                </div>
              </li>
            )}
          </ol>
        </div>
      </div>
    </div>
  );
};
